/**
 * @fileoverview Chat message and file utility functions.
 * @module logic/chat/chatMessageUtils
 */

import i18n from "../../i18n.js";
import { logger, sanitizeError } from "../../utils/logger.js";
import {
  BYTES_PER_KB,
  FILE_MAX_SIZE_BYTES,
  FILE_SIZE_DECIMAL_PLACES,
  FILE_SIZE_ZERO_BYTES,
  HOURS_PER_DAY,
  MS_PER_DAY,
  MS_PER_HOUR,
  MS_PER_MINUTE,
  SINGLE_HOUR_AGO_THRESHOLD,
  TIMESTAMP_ONE_HOUR_THRESHOLD,
  TIMESTAMP_ONE_MINUTE_THRESHOLD,
  YESTERDAY_MAX_DAYS,
} from "./chatConstants.js";

/** Maps i18n language codes to Intl locale strings for chat timestamps. */
export const CHAT_TIMESTAMP_LOCALE_BY_LANGUAGE = {
  en: "en-US",
  es: "es-ES",
  fr: "fr-FR",
};

const resolveChatTimestampLocale = (language) =>
  CHAT_TIMESTAMP_LOCALE_BY_LANGUAGE[language] ||
  CHAT_TIMESTAMP_LOCALE_BY_LANGUAGE[i18n.language] ||
  "es-ES";

/**
 * Uploads a file to S3 using a presigned URL.
 * @async
 * @param {File} file - File object to upload
 * @param {string} presignedUrl - Presigned S3 URL for upload
 * @returns {Promise<boolean>} True if upload successful
 */
export const uploadFileToS3 = async (file, presignedUrl) => {
  try {
    const response = await fetch(presignedUrl, {
      method: "PUT",
      body: file,
      headers: {
        "Content-Type": file.type,
      },
    });

    if (response.ok) {
      logger.info("File uploaded successfully to S3");
      return true;
    } else {
      logger.error("Failed to upload file to S3:", {
        data: response.statusText,
      });
      return false;
    }
  } catch (error) {
    logger.error("Error uploading file to S3:", { err: sanitizeError(error) });
    return false;
  }
};

/**
 * Validates a file for chat upload (type and size restrictions).
 * @param {File} file - File object to validate
 * @returns {Object} Object with isValid boolean and error message
 */
export const validateFile = (file) => {
  const allowedTypes = [
    "image/gif",
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/bmp",
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ];
  const maxSize = FILE_MAX_SIZE_BYTES;

  if (!allowedTypes.includes(file.type)) {
    return {
      isValid: false,
      error:
        "File type not allowed. Please upload GIF, JPEG, PNG, BMP, PDF, DOC, or DOCX files.",
    };
  }

  if (file.size > maxSize) {
    return {
      isValid: false,
      error: "File size must be less than 10MB.",
    };
  }

  return { isValid: true, error: "" };
};

/**
 * Extracts timestamp from a message object.
 * @param {Object} message - Message object
 * @returns {string|null} Timestamp string or null
 */
export const extractMessageTimestamp = (message) => {
  if (!message) {
    return null;
  }
  if (message.timestamp) {
    return message.timestamp;
  }
  if (message.createdAt) {
    return message.createdAt;
  }
  if (typeof message.SK === "string" && message.SK.startsWith("MESSAGE#")) {
    // Example: MESSAGE#2025-08-13T04:30:56.502Z → 2025-08-13T04:30:56.502Z
    return message.SK.slice("MESSAGE#".length);
  }
  return null;
};

/**
 * Formats a timestamp for display with relative time support.
 * @param {string|Date} timestamp - Timestamp to format
 * @param {string} [language=i18n.language] - Active i18n language code
 * @returns {string} Formatted timestamp string
 */
export const formatMessageTimestamp = (
  timestamp,
  language = i18n.language,
) => {
  const locale = resolveChatTimestampLocale(language);
  const parsed = typeof timestamp === "string" ? timestamp : String(timestamp);
  const messageDate = new Date(parsed);
  if (Number.isNaN(messageDate.getTime())) {
    try {
      const reparsed = new Date(parsed.replace(" ", "T"));
      if (!Number.isNaN(reparsed.getTime())) {
        return reparsed.toLocaleString(locale, {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      }
    } catch (_) {}
    return "";
  }
  const now = new Date();
  const diffInHours = (now - messageDate) / MS_PER_HOUR;
  const diffInDays = (now - messageDate) / MS_PER_DAY;

  if (diffInHours < HOURS_PER_DAY) {
    if (diffInHours < TIMESTAMP_ONE_HOUR_THRESHOLD) {
      const diffInMinutes = Math.floor((now - messageDate) / MS_PER_MINUTE);
      if (diffInMinutes < TIMESTAMP_ONE_MINUTE_THRESHOLD) {
        return i18n.t("components.notificationsList.secondsAgo");
      }
      return i18n.t("pages.chat.timestampMinutesAgo", {
        count: diffInMinutes,
      });
    }
    if (diffInHours < SINGLE_HOUR_AGO_THRESHOLD) {
      return i18n.t("pages.chat.timestampOneHourAgo");
    }
    return i18n.t("pages.chat.timestampHoursAgo", {
      count: Math.floor(diffInHours),
    });
  }

  const time = messageDate.toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  if (diffInDays < YESTERDAY_MAX_DAYS) {
    return i18n.t("pages.chat.timestampYesterday", { time });
  }

  const date = messageDate.toLocaleDateString(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
  return `${date} • ${time}`;
};

/**
 * Formats file size in bytes to human-readable format.
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted size string (e.g., "1.5 MB")
 */
export const formatFileSize = (bytes) => {
  if (bytes === FILE_SIZE_ZERO_BYTES) {
    return "0 Bytes";
  }
  const k = BYTES_PER_KB;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return (
    parseFloat((bytes / Math.pow(k, i)).toFixed(FILE_SIZE_DECIMAL_PLACES)) +
    " " +
    sizes[i]
  );
};

/**
 * Checks if a file's presigned URL has expired.
 * @param {string} fileExpiresAt - Expiration date string
 * @returns {boolean} True if file has expired
 */
export const isFileExpired = (fileExpiresAt) => {
  if (!fileExpiresAt) {
    return false;
  }
  const currentDate = new Date();
  const expirationDate = new Date(fileExpiresAt);

  return currentDate >= expirationDate;
};

/**
 * Determines file type and appropriate icon based on filename.
 * @param {string} fileName - Name of the file
 * @param {string} messageType - Message type (e.g., "FILE")
 * @returns {Object} Object with type and icon properties
 */
export const getFileTypeAndIcon = (fileName, messageType) => {
  // If messageType is explicitly set to "FILE", use that
  if (messageType === "FILE") {
    const fileExtension = fileName.split(".").pop()?.toLowerCase();

    // Image file extensions
    const imageExtensions = ["gif", "jpeg", "jpg", "png", "bmp"];
    if (imageExtensions.includes(fileExtension)) {
      return {
        type: "image",
        icon: "image",
      };
    }

    // Document file extensions
    const documentExtensions = ["pdf", "doc", "docx"];
    if (documentExtensions.includes(fileExtension)) {
      return {
        type: "document",
        icon: "file",
      };
    }

    // Default to file icon for other types
    return {
      type: "other",
      icon: "file",
    };
  }

  return { type: "text", icon: null };
};
